import { ElementRef, inject, Injectable, DOCUMENT } from '@angular/core';
import { fromEvent, mergeWith, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { IAppConfig } from '@interfaces/app-config/app-config.interface';

import {
  ITrackInViewport,
  ITrackInViewportAmplitudeData,
  ITrackInViewportData,
} from '@interfaces/track-in-viewport/track-in-viewport.interface';

@Injectable({
  providedIn: 'root',
})
export class ViewportTrackingService {
  public trackInViewport: ITrackInViewport;

  private collectedCardsData: ITrackInViewportData[] = [];
  private collectedCardsList: ElementRef[] = [];
  private trackInViewportData: Subject<ITrackInViewportAmplitudeData> = new Subject<ITrackInViewportAmplitudeData>();
  private cardAdded: Subject<void> = new Subject<void>();

  private window: Window = inject(Window);
  private document: Document = inject(DOCUMENT);
  private minPercentageCardInViewport = 70;

  public clearLists(): void {
    this.collectedCardsData = [];
    this.collectedCardsList = [];
    this.trackInViewportData.next({ ids: '', positions: '' });
  }

  public collectCard(id: string, cardComponent: ElementRef, position: number): void {
    if (!this.trackInViewport) {
      return;
    }
    this.collectedCardsData.push({ id, position });
    this.collectedCardsList.push(cardComponent);
    this.cardAdded.next();
  }

  public init(config: IAppConfig): void {
    this.subscribeOnInteractionEvents();
    this.sendInitialEvent();
    this.sendCollectedData(config);
  }

  private subscribeOnInteractionEvents(): void {
    fromEvent(this.window, 'scroll')
      .pipe(
        mergeWith(fromEvent(this.window, 'resize')),
        filter(() => !!this.trackInViewport)
      )
      .subscribe(() => this.checkElements());
  }

  private sendInitialEvent(): void {
    this.cardAdded
      .pipe(
        filter(() => !!this.trackInViewport),
        debounceTime(100)
      )
      .subscribe(() => this.checkElements());
  }

  private sendCollectedData(config: IAppConfig): void {
    this.trackInViewportData
      .pipe(
        distinctUntilChanged(),
        filter(
          (trackInViewportData: ITrackInViewportAmplitudeData) => !!trackInViewportData.ids && !!this.trackInViewport
        ),
        debounceTime(config.trackOnScrollWithDelay ? +config.trackOnScrollWithDelay : 2000)
      )
      .subscribe((trackInViewportData: ITrackInViewportAmplitudeData) => {
        this.trackInViewport.sendAmplitudeEvent(trackInViewportData);
      });
  }

  private checkElements(): void {
    const TrackInViewportData: ITrackInViewportAmplitudeData = this.collectedCardsList.reduce(
      (trackInViewportData: ITrackInViewportAmplitudeData, card: ElementRef, index: number) => {
        if (this.elementIsFullyInViewport(card.nativeElement)) {
          trackInViewportData.ids += trackInViewportData.ids
            ? `, ${this.collectedCardsData[index].id}`
            : this.collectedCardsData[index].id;
          trackInViewportData.positions += trackInViewportData.positions
            ? `, ${this.collectedCardsData[index].position}`
            : this.collectedCardsData[index].position.toString();
        }
        return trackInViewportData;
      },
      { ids: '', positions: '' }
    );

    this.trackInViewportData.next(TrackInViewportData);
  }

  private elementIsFullyInViewport(el: any): boolean {
    const slide = el.parentElement.parentElement;
    if (slide.classList.contains('swiper-slide') && !slide.classList.contains('swiper-slide-visible')) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const height: number = this.window.innerHeight || this.document.documentElement.clientHeight;
    const width: number = this.window.innerWidth || this.document.documentElement.clientWidth;
    const elHorizontalPosition: number = rect.x + (rect.width / 100) * this.minPercentageCardInViewport;
    const inViewToWidth: boolean =
      elHorizontalPosition > (rect.width / 100) * this.minPercentageCardInViewport && elHorizontalPosition < width;
    const inViewToTopHeight: boolean = (rect.height / 100) * this.minPercentageCardInViewport < rect.bottom;
    const inViewToBottomHeight: boolean = rect.top + (rect.height / 100) * this.minPercentageCardInViewport < height;
    return inViewToTopHeight && inViewToBottomHeight && inViewToWidth;
  }
}
